import {
  type MemberKind,
  type NewPerson,
  type PersonRow,
  parsePeopleCsv,
  termFor,
  timeOf,
  todayIso,
  useAdminActions,
  useAudit,
  useBellPeriods,
  useClassTimetable,
  useClasses,
  useCurrentContext,
  usePeople,
  useShelters,
  useTeachers,
  useTerms,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { MagnifyingGlassIcon } from '@phosphor-icons/react/MagnifyingGlass';
import { UploadSimpleIcon } from '@phosphor-icons/react/UploadSimple';
import QRCode from 'qrcode';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  EmptyState,
  Field,
  Modal,
  SecondaryButton,
  Segmented,
  Select,
  Spinner,
} from '../components/ui';
import {
  Block,
  GRAD,
  type Grad,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  PageHead,
  PillIcon,
  SUBJECT_GRAD,
  Tabs,
  grad,
  pillClass,
  ringSmall,
} from '../design/forms';
import { CheckIcon, PlusIcon, XIcon } from '../design/icons';
import { buttonClass } from '../design/ui';

/*
 * «Адміністрування» in «Форми 2» (28.10): the title on the paper with the four parts as a choice beside
 * it; each part one white block — the people with a search, the kinds as chips and the two actions on
 * top, the list in two columns with the first letters in a soft circle; the classes with their homeroom
 * teacher and shelter; the timetable as a week of soft subject cells; the log of actions as rows.
 */

type Tab = 'people' | 'classes' | 'timetable' | 'audit';
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт'];

const FIELD =
  'w-full rounded-[14px] bg-white text-[14px] text-ink outline-none ring-1 ring-[#E4E0D7] ring-inset transition-shadow placeholder:text-faint focus:ring-2 focus:ring-[var(--color-primary)]';

/** A person's kind as a colour. */
const KIND_TONE: Record<MemberKind, Grad> = {
  student: 'sky',
  guardian: 'butter',
  staff: 'lavender',
};

/** A label: a soft rectangle in its colour. */
function Label({ tone, children }: { tone: Grad; children: ReactNode }) {
  return (
    <span
      className="inline-flex h-6 shrink-0 items-center gap-1 rounded-[7px] px-2 text-[12px] leading-none font-medium whitespace-nowrap"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      {children}
    </span>
  );
}

/** The first letters of a name in a soft circle of the person's kind. */
function Initials({ name, tone }: { name: string; tone: Grad }) {
  const letters = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span
      aria-hidden
      className="flex size-9 shrink-0 items-center justify-center rounded-full text-[13px] font-medium"
      style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
    >
      {letters}
    </span>
  );
}

/** A filter chip, as in «Домашні»: the paper, the chosen one a shade darker. */
function FilterChip({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`inline-flex h-10 shrink-0 items-center gap-2 rounded-full px-4 text-[14px] transition-colors ${on ? 'font-medium' : 'hover:bg-[#EFECE5]'}`}
      style={{ backgroundColor: on ? '#E9E5DC' : PAPER, color: INK }}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------------------------
function InviteModal({
  person,
  schoolId,
  onClose,
}: {
  person: PersonRow;
  schoolId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const invite = useAdminActions(schoolId).invite;
  const [qr, setQr] = useState<string>();
  const { mutate } = invite;
  // one code per opening: React runs effects twice in development, and every new code revokes the last
  const asked = useRef(false);
  useEffect(() => {
    if (!asked.current) {
      asked.current = true;
      mutate(person.id);
    }
  }, [mutate, person.id]);
  const code = invite.data?.invite_code;
  const printed = code ? `${code.slice(0, 5)}-${code.slice(5)}` : '';
  const link = `${window.location.origin}/invite?code=${printed}`;
  useEffect(() => {
    if (code) void QRCode.toDataURL(link, { width: 240, margin: 1 }).then(setQr);
  }, [code, link]);

  return (
    <Modal title={t('admin.invite.title')} onClose={onClose}>
      {invite.isPending ? (
        <Spinner />
      ) : invite.isError ? (
        <EmptyState>{t('common.error')}</EmptyState>
      ) : (
        <div className="print-area space-y-3 text-center">
          <p
            className="font-display text-[22px] leading-7 font-light tracking-[-0.02em]"
            style={{ color: INK }}
          >
            {person.lastName} {person.firstName}
          </p>
          <p className="text-[13.5px]" style={{ color: INK_SOFT }}>
            {t('admin.invite.hint')}
          </p>
          {qr && (
            <span
              className="mx-auto block w-fit rounded-[22px] p-3"
              style={{ backgroundColor: PAPER }}
            >
              <img src={qr} alt="QR" className="size-48 rounded-[10px]" />
            </span>
          )}
          <p className="font-mono text-2xl tracking-widest" style={{ color: INK }}>
            {printed}
          </p>
          <p className="text-[12px] break-all" style={{ color: INK_SOFT }}>
            {link}
          </p>
          <p className="text-[12px]" style={{ color: INK_SOFT }}>
            {t('admin.invite.validUntil', {
              date: new Date(invite.data?.valid_until ?? '').toLocaleDateString('uk-UA'),
            })}
          </p>
          <SecondaryButton className="print:hidden" onClick={() => window.print()}>
            {t('admin.invite.print')}
          </SecondaryButton>
        </div>
      )}
    </Modal>
  );
}

function AddPersonModal({
  schoolId,
  people,
  onClose,
}: {
  schoolId: string;
  people: PersonRow[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const classes = useClasses(schoolId);
  const add = useAdminActions(schoolId).add;
  const [person, setPerson] = useState<NewPerson>({ kind: 'student', lastName: '', firstName: '' });
  const set = (patch: Partial<NewPerson>) => setPerson({ ...person, ...patch });
  const students = people.filter((p) => p.kind === 'student');
  return (
    <Modal title={t('admin.people.add')} onClose={onClose}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void add.mutateAsync(person).then(onClose);
        }}
      >
        <Segmented<MemberKind>
          value={person.kind}
          onChange={(kind) => set({ kind })}
          options={(['student', 'guardian', 'staff'] as const).map((k) => ({
            value: k,
            label: t(`roles.${k}`),
          }))}
        />
        <Field
          id="p-last"
          label={t('admin.people.lastName')}
          required
          value={person.lastName}
          onChange={(e) => set({ lastName: e.target.value })}
        />
        <Field
          id="p-first"
          label={t('admin.people.firstName')}
          required
          value={person.firstName}
          onChange={(e) => set({ firstName: e.target.value })}
        />
        <Field
          id="p-middle"
          label={t('admin.people.middleName')}
          value={person.middleName ?? ''}
          onChange={(e) => set({ middleName: e.target.value })}
        />
        {person.kind === 'student' && (
          <>
            <Field
              id="p-birth"
              type="date"
              label={t('admin.people.birthDate')}
              value={person.birthDate ?? ''}
              onChange={(e) => set({ birthDate: e.target.value })}
            />
            <Select
              label={t('schedule.class')}
              value={person.classId ?? ''}
              onChange={(e) => set({ classId: e.target.value || undefined })}
            >
              <option value="">—</option>
              {(classes.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
          </>
        )}
        {person.kind === 'guardian' && (
          <Select
            label={t('admin.people.child')}
            value={person.childId ?? ''}
            onChange={(e) => set({ childId: e.target.value || undefined })}
          >
            <option value="">—</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>
                {s.lastName} {s.firstName} {s.classLabel ? `(${s.classLabel})` : ''}
              </option>
            ))}
          </Select>
        )}
        {add.isError && (
          <p className="text-[13.5px]" style={{ color: GRAD.coral.ink }}>
            {t('common.error')}
          </p>
        )}
        <Button
          type="submit"
          disabled={!person.lastName.trim() || !person.firstName.trim() || add.isPending}
        >
          {t('admin.people.save')}
        </Button>
      </form>
    </Modal>
  );
}

function ImportModal({ schoolId, onClose }: { schoolId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const classes = useClasses(schoolId);
  const importCsv = useAdminActions(schoolId).importCsv;
  const [text, setText] = useState('');
  const parsed = useMemo(() => parsePeopleCsv(text, classes.data ?? []), [text, classes.data]);
  const result = importCsv.data;
  const errors = [...parsed.errors, ...(result?.errors ?? [])];
  return (
    <Modal title={t('admin.import.title')} onClose={onClose}>
      <p className="mb-3 text-[13.5px] leading-5" style={{ color: INK_SOFT }}>
        {t('admin.import.format')}
      </p>
      <textarea
        className={`${FIELD} h-40 p-3 font-mono text-[12px]`}
        placeholder={'учень;Коваль;Марко;Олегович;2013-04-02;7-Б\nбатьки;Коваль;Олена;Іванівна'}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <input
        type="file"
        accept=".csv,text/csv,text/plain"
        className="mt-3 block text-[13px] file:mr-3 file:h-9 file:cursor-pointer file:rounded-full file:border-0 file:bg-[#F5F3EF] file:px-4 file:text-[13px] file:font-medium"
        style={{ color: INK_SOFT }}
        onChange={(e) => void e.target.files?.[0]?.text().then(setText)}
      />
      <p className="mt-3 flex flex-wrap gap-1.5">
        <Label tone="lavender">{t('admin.import.ready', { count: parsed.people.length })}</Label>
        {parsed.errors.length > 0 && (
          <Label tone="coral">{t('admin.import.bad', { count: parsed.errors.length })}</Label>
        )}
      </p>
      {errors.length > 0 && (
        <ul
          className="mt-2 max-h-24 overflow-y-auto text-[12px] leading-[18px]"
          style={{ color: GRAD.coral.ink }}
        >
          {errors.map((e) => (
            <li key={`${e.line}-${e.message}`}>
              {t('admin.import.line', { line: e.line })}:{' '}
              {t(`admin.import.errors.${e.message}`, { defaultValue: e.message })}
            </li>
          ))}
        </ul>
      )}
      {result && (
        <p className="mt-2 text-[13.5px]" style={{ color: GRAD.mint.ink }}>
          {t('admin.import.done', { count: result.added })}
        </p>
      )}
      <Button
        className="mt-4"
        disabled={parsed.people.length === 0 || importCsv.isPending}
        onClick={() => importCsv.mutate(parsed.people)}
      >
        {t('admin.import.run')}
      </Button>
    </Modal>
  );
}

function PeopleTab({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const people = usePeople(schoolId);
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<MemberKind | 'all'>('all');
  const [inviting, setInviting] = useState<PersonRow>();
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const list = (people.data ?? []).filter(
    (p) =>
      (kind === 'all' || p.kind === kind) &&
      `${p.lastName} ${p.firstName} ${p.classLabel ?? ''}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  return (
    <Block>
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-[220px] flex-1">
          <MagnifyingGlassIcon
            size={16}
            weight="bold"
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2"
            color={INK_FAINT}
          />
          <input
            className={`${FIELD} h-11 pr-3.5 pl-10`}
            placeholder={t('admin.people.search')}
            aria-label={t('admin.people.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <button type="button" className={pillClass('light')} onClick={() => setImporting(true)}>
          {t('admin.import.title')}
          <PillIcon icon={UploadSimpleIcon} />
        </button>
        <button type="button" className={pillClass('dark')} onClick={() => setAdding(true)}>
          {t('admin.people.add')}
          <PillIcon icon={PlusIcon} />
        </button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {(['all', 'student', 'guardian', 'staff'] as const).map((k) => (
          <FilterChip key={k} on={kind === k} onClick={() => setKind(k)}>
            {k === 'all' ? t('admin.people.all') : t(`roles.${k}`)}
          </FilterChip>
        ))}
        <span className="ml-auto pl-2 text-[13px]" style={{ color: INK_FAINT }}>
          {t('admin.people.count', { count: list.length })}
        </span>
      </div>
      {people.isPending ? (
        <Spinner />
      ) : (
        <ul className="mt-4 grid gap-x-8 xl:grid-cols-2">
          {list.slice(0, 200).map((p) => (
            <li
              key={p.id}
              className="flex min-h-[60px] items-center gap-3 border-t border-[#E4E0D7] py-2"
            >
              <Initials name={`${p.firstName} ${p.lastName}`} tone={KIND_TONE[p.kind]} />
              <span className="min-w-0 flex-1">
                <span
                  className="block truncate text-[15px] leading-5 font-medium"
                  style={{ color: INK }}
                >
                  {p.lastName} {p.firstName} {p.middleName ?? ''}
                </span>
                <span className="block truncate text-[12.5px]" style={{ color: INK_SOFT }}>
                  {p.kind === 'staff'
                    ? p.roles.map((r) => t(`roles.${r as 'teacher'}`)).join(', ')
                    : t(`roles.${p.kind}`)}
                  {p.classLabel ? ` · ${p.classLabel}` : ''}
                </span>
              </span>
              {p.hasAccount ? (
                <Label tone="mint">
                  <CheckIcon size={11} weight="bold" aria-hidden />
                  {t('admin.people.hasAccount')}
                </Label>
              ) : (
                <button
                  type="button"
                  className={buttonClass('outline', 'sm')}
                  onClick={() => setInviting(p)}
                >
                  {t('admin.people.invite')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {inviting && (
        <InviteModal person={inviting} schoolId={schoolId} onClose={() => setInviting(undefined)} />
      )}
      {adding && (
        <AddPersonModal
          schoolId={schoolId}
          people={people.data ?? []}
          onClose={() => setAdding(false)}
        />
      )}
      {importing && <ImportModal schoolId={schoolId} onClose={() => setImporting(false)} />}
    </Block>
  );
}

// ---------------------------------------------------------------------------------------------
// Classes, timetable, audit
// ---------------------------------------------------------------------------------------------
function ClassesTab({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const classes = useClasses(schoolId);
  const teachers = useTeachers(schoolId);
  const shelters = useShelters(schoolId);
  const update = useAdminActions(schoolId).updateClass;
  return (
    <Block>
      {classes.isPending ? (
        <Spinner />
      ) : (
        <table className="w-full text-[14px]">
          <thead>
            <tr className="text-left text-[12.5px]" style={{ color: INK_FAINT }}>
              <th className="w-20 pb-2 font-medium">{t('schedule.class')}</th>
              <th className="pb-2 font-medium">{t('admin.classes.homeroom')}</th>
              <th className="pb-2 pl-3 font-medium">{t('alarm.shelters')}</th>
            </tr>
          </thead>
          <tbody>
            {(classes.data ?? []).map((c) => (
              <tr key={c.id} className="border-t border-[#E4E0D7]">
                <td
                  className="py-2.5 font-display text-[22px] leading-7 font-light tracking-[-0.02em]"
                  style={{ color: INK }}
                >
                  {c.label}
                </td>
                <td className="py-2.5">
                  <Select
                    className="w-full"
                    value={c.homeroomId ?? ''}
                    onChange={(e) =>
                      update.mutate({ classId: c.id, homeroomId: e.target.value || null })
                    }
                  >
                    <option value="">—</option>
                    {(teachers.data ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.last_name} {p.first_name}
                      </option>
                    ))}
                  </Select>
                </td>
                <td className="py-2.5 pl-3">
                  <Select
                    className="w-full"
                    value={c.shelterId ?? ''}
                    onChange={(e) =>
                      update.mutate({ classId: c.id, shelterId: e.target.value || null })
                    }
                  >
                    <option value="">—</option>
                    {(shelters.data ?? []).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Block>
  );
}

function TimetableTab({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const classes = useClasses(schoolId);
  const terms = useTerms(schoolId);
  const bells = useBellPeriods(schoolId);
  const actions = useAdminActions(schoolId);
  const [classId, setClassId] = useState<string>();
  const [termId, setTermId] = useState<string>();
  const klass = classId ?? classes.data?.[0]?.id;
  const term = termId ?? (terms.data && termFor(terms.data, todayIso())?.id);
  const timetable = useClassTimetable(klass, term);
  const [adding, setAdding] = useState<{ weekday: number; period: number }>();
  const [courseId, setCourseId] = useState('');

  return (
    <Block>
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <Select
          className="h-11"
          aria-label={t('schedule.class')}
          value={klass ?? ''}
          onChange={(e) => setClassId(e.target.value)}
        >
          {(classes.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </Select>
        <Select className="h-11" value={term ?? ''} onChange={(e) => setTermId(e.target.value)}>
          {(terms.data ?? []).map((x) => (
            <option key={x.id} value={x.id}>
              {t('journal.term', { n: x.number })}
            </option>
          ))}
        </Select>
        <button
          type="button"
          className={`${pillClass('light')} disabled:opacity-50`}
          disabled={!term || actions.generateLessons.isPending}
          onClick={() => term && actions.generateLessons.mutate(term)}
        >
          {t('admin.timetable.generate')}
          <PillIcon icon={PlusIcon} />
        </button>
        {actions.generateLessons.data !== undefined && (
          <Label tone="mint">
            {t('admin.timetable.generated', { count: actions.generateLessons.data })}
          </Label>
        )}
      </div>
      {timetable.isPending ? (
        <Spinner />
      ) : (
        <div className="-mx-1 overflow-x-auto">
          <table className="w-full min-w-[640px] table-fixed text-[13px]">
            <thead>
              <tr>
                <th className="w-16" />
                {WEEKDAYS.map((d) => (
                  <th
                    key={d}
                    className="px-1 pb-2 text-left text-[13px] font-medium"
                    style={{ color: INK_SOFT }}
                  >
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(bells.data ?? []).map((b) => (
                <tr key={b.number} className="border-t border-[#E4E0D7] align-top">
                  <td className="px-1 py-2">
                    <span
                      className="block font-display text-[20px] leading-6 font-light"
                      style={{ color: INK }}
                    >
                      {b.number}
                    </span>
                    <span className="text-[11.5px]" style={{ color: INK_FAINT }}>
                      {b.starts_at.slice(0, 5)}
                    </span>
                  </td>
                  {WEEKDAYS.map((_, i) => {
                    const slots = (timetable.data?.slots ?? []).filter(
                      (s) => s.weekday === i + 1 && s.period === b.number,
                    );
                    return (
                      <td key={i} className="px-1 py-2">
                        <span className="flex flex-col gap-1">
                          {slots.map((s) => {
                            const tone = SUBJECT_GRAD[subjectLook(s.label).tone];
                            return (
                              <span
                                key={s.id}
                                className="flex items-start justify-between gap-1 rounded-[10px] py-1.5 pr-1 pl-2 text-[12px] leading-4"
                                style={{ backgroundImage: grad(tone), color: GRAD[tone].ink }}
                              >
                                <span className="min-w-0">{s.label}</span>
                                <button
                                  type="button"
                                  aria-label="×"
                                  className="flex size-5 shrink-0 items-center justify-center rounded-full opacity-60 transition-opacity hover:bg-white/70 hover:opacity-100"
                                  onClick={() => actions.deleteSlot.mutate(s.id)}
                                >
                                  <XIcon size={10} weight="bold" aria-hidden />
                                </button>
                              </span>
                            );
                          })}
                          <button
                            type="button"
                            aria-label={t('admin.timetable.add')}
                            title={t('admin.timetable.add')}
                            className={`${ringSmall.replace('size-8', 'size-7')} opacity-40 transition-opacity hover:opacity-100`}
                            onClick={() => setAdding({ weekday: i + 1, period: b.number })}
                          >
                            <PlusIcon size={12} weight="bold" aria-hidden />
                          </button>
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding && term && (
        <Modal
          title={`${WEEKDAYS[adding.weekday - 1]}, ${t('schedule.period', { n: adding.period })}`}
          onClose={() => setAdding(undefined)}
        >
          <Select
            className="h-11 w-full"
            value={courseId}
            onChange={(e) => setCourseId(e.target.value)}
          >
            <option value="">—</option>
            {(timetable.data?.courses ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </Select>
          <Button
            className="mt-4"
            disabled={!courseId}
            onClick={() =>
              void actions.addSlot
                .mutateAsync({
                  termId: term,
                  courseId,
                  weekday: adding.weekday,
                  period: adding.period,
                })
                .then(() => setAdding(undefined))
            }
          >
            {t('admin.timetable.add')}
          </Button>
        </Modal>
      )}
    </Block>
  );
}

const AUDIT_TONE: Record<string, Grad> = { insert: 'mint', update: 'sky', delete: 'coral' };

function AuditTab({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const audit = useAudit(schoolId);
  return (
    <Block>
      {audit.isPending ? (
        <Spinner />
      ) : (audit.data ?? []).length === 0 ? (
        <EmptyState>—</EmptyState>
      ) : (
        <ul className="flex flex-col">
          {(audit.data ?? []).map((a) => (
            <li
              key={a.id}
              className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-[#E4E0D7] py-2.5 text-[14px] first:border-t-0"
            >
              <span
                className="w-36 shrink-0 text-[12.5px] tabular-nums"
                style={{ color: INK_SOFT }}
              >
                {new Date(a.at).toLocaleDateString('uk-UA')} {timeOf(a.at)}
              </span>
              <span className="w-48 shrink-0 truncate font-medium" style={{ color: INK }}>
                {a.actor_name ?? t('admin.audit.system')}
              </span>
              <span className="flex min-w-0 items-center gap-2" style={{ color: INK_SOFT }}>
                <Label tone={AUDIT_TONE[a.action] ?? 'stone'}>
                  {t(`admin.audit.actions.${a.action as 'insert'}`)}
                </Label>
                {t(`admin.audit.entities.${a.entity}`, { defaultValue: a.entity })}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Block>
  );
}

export function AdminPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const [tab, setTab] = useState<Tab>('people');
  if (ctx.kind !== 'admin')
    return (
      <div className="flex flex-col gap-4">
        <PageHead title={t('admin.title')} />
        <Block>
          <EmptyState>{t('admin.onlyAdmin')}</EmptyState>
        </Block>
      </div>
    );
  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('admin.title')}>
        <div className="max-w-full overflow-x-auto">
          <Tabs<Tab>
            value={tab}
            label={t('admin.title')}
            onChange={setTab}
            options={(['people', 'classes', 'timetable', 'audit'] as const).map((k) => ({
              id: k,
              label: t(`admin.tabs.${k}`),
            }))}
          />
        </div>
      </PageHead>
      {tab === 'people' && <PeopleTab schoolId={ctx.schoolId} />}
      {tab === 'classes' && <ClassesTab schoolId={ctx.schoolId} />}
      {tab === 'timetable' && <TimetableTab schoolId={ctx.schoolId} />}
      {tab === 'audit' && <AuditTab schoolId={ctx.schoolId} />}
    </div>
  );
}
